'use client';

import { useEffect } from 'react';

import {
  type BeforeInstallPromptEvent,
  clearDeferredPwaInstallPrompt,
  markPwaInstalled,
  PWA_INSTALLED_EVENT,
  PWA_INSTALL_PROMPT_READY_EVENT,
  setDeferredPwaInstallPrompt,
} from '~/shared/pwa-install-state';


const isStandalone = (): boolean => {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };

  return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
};


const PwaBootstrap = () => {
  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPwaInstallPrompt(event as BeforeInstallPromptEvent);
      window.dispatchEvent(new Event(PWA_INSTALL_PROMPT_READY_EVENT));
    };

    const handleAppInstalled = () => {
      clearDeferredPwaInstallPrompt();
      markPwaInstalled();
      window.dispatchEvent(new Event(PWA_INSTALLED_EVENT));
    };

    const standaloneMedia = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = () => {
      if (isStandalone()) markPwaInstalled();
    };

    if (isStandalone()) markPwaInstalled();

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    standaloneMedia.addEventListener?.('change', handleDisplayModeChange);

    if ('serviceWorker' in navigator) {
      const isSecureRuntime = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

      if (isSecureRuntime) {
        navigator.serviceWorker
          .register('/pwa-sw.js', {
            scope: '/',
            updateViaCache: 'none',
          })
          .then(async (registration) => {
            await registration.update().catch(() => undefined);
            await navigator.serviceWorker.ready.catch(() => undefined);

          })
          .catch((error) => {
            console.warn('PWA service worker registration failed:', error);
          });
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      standaloneMedia.removeEventListener?.('change', handleDisplayModeChange);
    };
  }, []);

  return null;
};

export default PwaBootstrap;
